import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { useAuth } from '../context/AuthContext';
import { ticketService } from '../services/ticketService';
import { Ticket, TicketStatus } from '../types';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    pendingApproval: 0,
    rejected: 0
  });

  const [previousMonthStats, setPreviousMonthStats] = useState({
    total: 0,
    new: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    pendingApproval: 0,
    rejected: 0
  });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const data = await ticketService.getTickets();
      setTickets(data.tickets);
      
      // Izračunaj statistiku
      const newCount = data.tickets.filter(t => t.status === TicketStatus.NEW).length;
      const inProgressCount = data.tickets.filter(t => t.status === TicketStatus.IN_PROGRESS).length;
      const resolvedCount = data.tickets.filter(t => t.status === TicketStatus.RESOLVED).length;
      const closedCount = data.tickets.filter(t => t.status === TicketStatus.CLOSED).length;
      const pendingApprovalCount = data.tickets.filter(t => t.status === TicketStatus.PENDING_APPROVAL).length;
      const rejectedCount = data.tickets.filter(t => t.status === TicketStatus.REJECTED).length;
      
      setStats({
        total: data.tickets.length,
        new: newCount,
        inProgress: inProgressCount,
        resolved: resolvedCount,
        closed: closedCount,
        pendingApproval: pendingApprovalCount,
        rejected: rejectedCount
      });

      // Učitaj statistiku prethodnog meseca samo za SUPER_ADMIN
      if (user?.role === 'SUPER_ADMIN') {
        await loadPreviousMonthStats();
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousMonthStats = async () => {
    try {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      
      // Ovo je mock podatak - u realnoj aplikaciji bi pozvali API
      // Za sada ćemo simulirati podatke prethodnog meseca
      const mockPreviousStats = {
        total: Math.max(0, stats.total - Math.floor(Math.random() * 5)),
        new: Math.max(0, stats.new - Math.floor(Math.random() * 3)),
        inProgress: Math.max(0, stats.inProgress - Math.floor(Math.random() * 2)),
        resolved: Math.max(0, stats.resolved - Math.floor(Math.random() * 4)),
        closed: Math.max(0, stats.closed - Math.floor(Math.random() * 2)),
        pendingApproval: Math.max(0, stats.pendingApproval - Math.floor(Math.random() * 2)),
        rejected: Math.max(0, stats.rejected - Math.floor(Math.random() * 1))
      };
      
      setPreviousMonthStats(mockPreviousStats);
    } catch (error) {
      console.error('Error loading previous month stats:', error);
    }
  };

  // Funkcija za izračunavanje promene u odnosu na prethodni mesec
  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const chartData = {
    labels: ['New', 'In Progress', 'Resolved', 'Other'],
    datasets: [
      {
        data: [
          stats.new,
          stats.inProgress,
          stats.resolved,
          stats.total - stats.new - stats.inProgress - stats.resolved
        ],
        backgroundColor: ['#3B82F6', '#F59E0B', '#10B981', '#6B7280'],
        borderWidth: 0
      }
    ]
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          font: {
            size: 12,
            family: 'Inter'
          }
        }
      }
    }
  };

  const statusBodyTemplate = (rowData: Ticket) => {
    const severityMap: any = {
      PENDING_APPROVAL: 'warning',
      REJECTED: 'danger',
      NEW: 'info',
      OPEN: 'info',
      IN_PROGRESS: 'warning',
      PENDING: 'warning',
      RESOLVED: 'success',
      CLOSED: 'secondary',
      CANCELLED: 'danger'
    };

    return <Tag value={rowData.status.replace(/_/g, ' ')} severity={severityMap[rowData.status] || 'info'} />;
  };

  return (
    <div className="dashboard-container">
      {/* Welcome Header */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Welcome, {user?.firstName}! 👋</h1>
          <p className="dashboard-subtitle">Here is an overview of your tickets and activity</p>
        </div>
        <Button 
          label="New Ticket" 
          icon="pi pi-plus" 
          className="p-button-success"
          onClick={() => navigate('/tickets/new')}
          style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none'
          }}
        />
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <i className="pi pi-ticket"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.total}</h3>
            <p className="stat-label">Total Tickets</p>
          </div>
          {user?.role === 'SUPER_ADMIN' ? (
            <div className={`stat-trend ${calculateChange(stats.total, previousMonthStats.total) >= 0 ? 'up' : 'down'}`}>
              <i className={`pi ${calculateChange(stats.total, previousMonthStats.total) >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
              <span>{Math.abs(calculateChange(stats.total, previousMonthStats.total))}%</span>
            </div>
          ) : (
            <div className="stat-simple">
              <i className="pi pi-chart-line"></i>
            </div>
          )}
        </div>

        <div className="stat-card new">
          <div className="stat-icon">
            <i className="pi pi-plus-circle"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.new}</h3>
            <p className="stat-label">New Tickets</p>
          </div>
          {user?.role === 'SUPER_ADMIN' ? (
            <div className={`stat-trend ${calculateChange(stats.new, previousMonthStats.new) >= 0 ? 'up' : 'down'}`}>
              <i className={`pi ${calculateChange(stats.new, previousMonthStats.new) >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
              <span>{Math.abs(calculateChange(stats.new, previousMonthStats.new))}%</span>
            </div>
          ) : (
            <div className="stat-simple">
              <i className="pi pi-plus"></i>
            </div>
          )}
        </div>

        <div className="stat-card progress">
          <div className="stat-icon">
            <i className="pi pi-sync"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.inProgress}</h3>
            <p className="stat-label">In Progress</p>
          </div>
          {user?.role === 'SUPER_ADMIN' ? (
            <div className={`stat-trend ${calculateChange(stats.inProgress, previousMonthStats.inProgress) >= 0 ? 'up' : 'down'}`}>
              <i className={`pi ${calculateChange(stats.inProgress, previousMonthStats.inProgress) >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
              <span>{Math.abs(calculateChange(stats.inProgress, previousMonthStats.inProgress))}%</span>
            </div>
          ) : (
            <div className="stat-simple">
              <i className="pi pi-sync"></i>
            </div>
          )}
        </div>

        <div className="stat-card resolved">
          <div className="stat-icon">
            <i className="pi pi-check-circle"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.resolved}</h3>
            <p className="stat-label">Resolved</p>
          </div>
          {user?.role === 'SUPER_ADMIN' ? (
            <div className={`stat-trend ${calculateChange(stats.resolved, previousMonthStats.resolved) >= 0 ? 'up' : 'down'}`}>
              <i className={`pi ${calculateChange(stats.resolved, previousMonthStats.resolved) >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
              <span>{Math.abs(calculateChange(stats.resolved, previousMonthStats.resolved))}%</span>
            </div>
          ) : (
            <div className="stat-simple">
              <i className="pi pi-check"></i>
            </div>
          )}
        </div>

        {user?.role === 'SUPER_ADMIN' && (
          <>
            <div className="stat-card pending">
              <div className="stat-icon">
                <i className="pi pi-clock"></i>
              </div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.pendingApproval}</h3>
                <p className="stat-label">Pending Approval</p>
              </div>
              <div className={`stat-trend ${calculateChange(stats.pendingApproval, previousMonthStats.pendingApproval) >= 0 ? 'up' : 'down'}`}>
                <i className={`pi ${calculateChange(stats.pendingApproval, previousMonthStats.pendingApproval) >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
                <span>{Math.abs(calculateChange(stats.pendingApproval, previousMonthStats.pendingApproval))}%</span>
              </div>
            </div>

            <div className="stat-card closed">
              <div className="stat-icon">
                <i className="pi pi-lock"></i>
              </div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.closed}</h3>
                <p className="stat-label">Closed</p>
              </div>
              <div className={`stat-trend ${calculateChange(stats.closed, previousMonthStats.closed) >= 0 ? 'up' : 'down'}`}>
                <i className={`pi ${calculateChange(stats.closed, previousMonthStats.closed) >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
                <span>{Math.abs(calculateChange(stats.closed, previousMonthStats.closed))}%</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="content-grid">
        <div className="tickets-section">
          <Card className="modern-card" title={`Latest Tickets (${Math.min(tickets.length, 5)})`}>
            <DataTable
              value={tickets.slice(0, 5)}
              loading={loading}
              rows={5}
              onRowClick={(e) => navigate(`/tickets/${e.data._id}`)}
              className="modern-table"
              emptyMessage="No tickets"
            >
              <Column field="ticketNumber" header="Number" style={{ width: '100px' }} />
              <Column field="title" header="Title" />
              <Column field="status" header="Status" body={statusBodyTemplate} style={{ width: '150px' }} />
            </DataTable>
            <div className="view-all-tickets-container">
              <Button 
                label="View all tickets" 
                icon="pi pi-arrow-right" 
                iconPos="right"
                className="p-button-text p-button-plain view-all-btn"
                onClick={() => navigate('/tickets')}
              />
            </div>
          </Card>
        </div>

        <div className="chart-section">
          <Card className="modern-card" title="Ticket Statistics">
            <Chart type="doughnut" data={chartData} options={chartOptions} />
          </Card>
        </div>
      </div>

      <style>{`
        .dashboard-container {
          animation: fadeIn 0.4s ease;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .dashboard-title {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .dashboard-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0.5rem 0 0 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #10b981, #059669);
        }

        .stat-card.total::before { background: linear-gradient(90deg, #3b82f6, #2563eb); }
        .stat-card.new::before { background: linear-gradient(90deg, #8b5cf6, #7c3aed); }
        .stat-card.progress::before { background: linear-gradient(90deg, #f59e0b, #d97706); }
        .stat-card.resolved::before { background: linear-gradient(90deg, #10b981, #059669); }
        .stat-card.pending::before { background: linear-gradient(90deg, #f59e0b, #d97706); }
        .stat-card.closed::before { background: linear-gradient(90deg, #6b7280, #4b5563); }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 1rem;
        }

        .stat-card.total .stat-icon { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
        .stat-card.new .stat-icon { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
        .stat-card.progress .stat-icon { background: rgba(245, 158, 11, 0.1); color: #f59e0b; }
        .stat-card.resolved .stat-icon { background: rgba(16, 185, 129, 0.1); color: #10b981; }
        .stat-card.pending .stat-icon { background: rgba(245, 158, 11, 0.1); color: #f59e0b; }
        .stat-card.closed .stat-icon { background: rgba(107, 114, 128, 0.1); color: #6b7280; }

        .stat-content {
          margin-bottom: 1rem;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0.5rem 0 0 0;
          font-weight: 500;
        }

        .stat-trend {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          font-weight: 600;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
        }

        .stat-trend.up {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .stat-trend.down {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .stat-simple {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
          font-size: 1rem;
        }


        .content-grid {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 1.5rem;
        }

        @media (max-width: 1024px) {
          .content-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .dashboard-title {
            font-size: 1.45rem;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
            margin-bottom: 0.65rem;
          }

          .modern-table {
            overflow-x: auto;
          }

          .p-chart {
            max-width: 100%;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }

        .modern-card {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          border-radius: 16px;
          margin-bottom: 1.5rem;
          overflow: hidden;
        }

        .modern-card .p-card-header {
          padding: 1.5rem 1.5rem 1rem 1.5rem;
          background: #fafbfc;
          border-bottom: 2px solid #f3f4f6;
        }

        .modern-card .p-card-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modern-card .p-card-title::before {
          content: '📋';
          font-size: 1.5rem;
        }

        .modern-card .p-card-body {
          padding: 0;
        }

        .modern-card .p-card-content {
          padding: 0;
        }

        .modern-table {
          border-radius: 0;
        }

        .modern-table .p-datatable-wrapper {
          border-radius: 0;
        }

        .modern-table .p-datatable-thead > tr > th {
          background: #f9fafb;
          border-color: #e5e7eb;
          padding: 1rem 1.25rem;
          font-weight: 700;
          color: #374151;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-top: none;
        }

        .modern-table .p-datatable-tbody > tr {
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid #f3f4f6;
        }

        .modern-table .p-datatable-tbody > tr > td {
          padding: 1.25rem 1.25rem;
          border-color: #f3f4f6;
          font-size: 0.95rem;
        }

        .modern-table .p-datatable-tbody > tr:hover {
          background: #f0fdf4 !important;
          transform: translateX(4px);
          box-shadow: inset 4px 0 0 #10b981;
        }

        .modern-table .p-datatable-tbody > tr:last-child {
          border-bottom: none;
        }

        .modern-table .p-datatable-emptymessage > td {
          padding: 3rem 1.25rem;
          text-align: center;
          color: #9ca3af;
          font-size: 1rem;
          font-style: italic;
        }

        .modern-table .p-datatable-emptymessage > td::before {
          content: '📭';
          display: block;
          font-size: 3rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .view-all-tickets-container {
          padding: 1.25rem 1.5rem;
          background: #fafbfc;
          border-top: 2px solid #f3f4f6;
          text-align: center;
        }

        .view-all-btn {
          color: white !important;
          font-weight: 600 !important;
          transition: all 0.2s ease !important;
          padding: 0.75rem 1.5rem !important;
        }

        .view-all-btn:hover {
          background: #f0fdf4 !important;
          color: white !important;
          border-radius: 8px !important;
        }

        .view-all-btn .p-button-icon {
          transition: transform 0.2s ease;
        }

        .view-all-btn:hover .p-button-icon {
          transform: translateX(4px);
        }

        .modern-table .p-tag {
          padding: 0.4rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 700;
          border-radius: 8px;
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }

        .modern-table .p-tag.p-tag-success {
          background: #d1fae5;
          color: #065f46;
        }

        .modern-table .p-tag.p-tag-info {
          background: #dbeafe;
          color: #1e40af;
        }

        .modern-table .p-tag.p-tag-warning {
          background: #fef3c7;
          color: #92400e;
        }

        .modern-table .p-tag.p-tag-danger {
          background: #fee2e2;
          color: #991b1b;
        }

        .modern-table .p-tag.p-tag-secondary {
          background: #f3f4f6;
          color: #4b5563;
        }

        .chart-section {
          display: flex;
          flex-direction: column;
        }

        .quick-actions-card {
          flex: 1;
        }

        .quick-actions {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .quick-action-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          padding: 1.25rem 1.25rem;
          background: #ffffff;
          border: 2px solid #e5e7eb;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          min-height: 98px;
        }

        .quick-action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.12);
          border-color: #10b981;
        }

        .quick-action-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e5e7eb;
          background: #f0fdf4;
          color: #059669;
          transition: all 0.2s ease;
        }

        .quick-action-icon i {
          font-size: 1.35rem;
          line-height: 1;
        }

        .quick-action-label {
          text-align: center;
        }

        .quick-action-btn.primary .quick-action-icon {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-color: #10b981;
          color: white;
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.25);
        }

        .quick-action-btn.secondary .quick-action-icon {
          background: #f3f4f6;
          border-color: #e5e7eb;
          color: #374151;
        }

        .quick-action-btn.primary:hover .quick-action-icon {
          filter: brightness(1.02);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard; 